import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  User,
  Briefcase,
  GraduationCap,
  Target,
  Languages,
  FileText,
  Palette,
  Save,
  Plus,
  Edit2,
  Trash2,
  X,
  Link as LinkIcon,
  MapPin,
  Phone,
  Calendar,
  Building,
  FolderGit2,
  ExternalLink,
  Github,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '@/stores/authStore'
import { toast } from '@/stores/toastStore'
import api from '@/services/api'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Textarea from '@/components/common/Textarea'
import Card from '@/components/common/Card'
import Modal from '@/components/common/Modal'
import FileUpload from '@/components/common/FileUpload'
import UsernameSelector from '@/components/profile/settings/UsernameSelector'
import ThemeSelector from '@/components/profile/settings/ThemeSelector'
import ColorSchemeSelector from '@/components/profile/settings/ColorSchemeSelector'
import AccessibilitySettings from '@/components/profile/settings/AccessibilitySettings'
import PrivacyControls from '@/components/profile/settings/PrivacyControls'
import ShareTools from '@/components/profile/settings/ShareTools'
import DeleteAccountSection from '@/components/profile/settings/DeleteAccountSection'
import SkillAutocomplete from '@/components/common/SkillAutocomplete'
import type {
  ProfileSettings,
  University,
  Institution,
  WorkExperience,
  SkillSearchResult,
} from '@/types'
import { getProfileSettings } from '@/utils/profileDefaults'
import { useUpdateProfileSettings } from '@/hooks/useUpdateProfileSettings'
import { universityService } from '@/services/universityService'
import { format } from 'date-fns'

// Basic Info Schema
const basicInfoSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  jobTitle: z.string().min(1, 'Job title is required'),
  location: z.string().min(1, 'Location is required'),
  bio: z.string().max(500, 'Bio must be less than 500 characters').optional(),
  phone: z.string().optional(),
  linkedIn: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  github: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  portfolio: z.string().url('Must be a valid URL').optional().or(z.literal('')),
})

type BasicInfoFormData = z.infer<typeof basicInfoSchema>

// Experience Schema (uses unified API field names)
const experienceSchema = z.object({
  companyName: z.string().min(1, 'Company is required'),
  jobTitle: z.string().min(1, 'Job title is required'),
  location: z.string().optional(),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().optional(),
  isCurrent: z.boolean(),
  description: z.string().optional(),
})

type ExperienceFormData = z.infer<typeof experienceSchema>

// Education Schema
const educationSchema = z
  .object({
    // Institution field is now optional (can use either free-text or structured selection)
    institution: z.string().optional(),
    universityId: z.string().optional(),
    institutionId: z.string().optional(),
    degree: z.string().min(1, 'Degree is required'),
    fieldOfStudy: z.string().min(1, 'Field of study is required'),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().optional(),
    description: z.string().optional(),
  })
  .refine((data) => data.institution || data.institutionId, {
    message: 'Either institution name or institution selection is required',
    path: ['institution'],
  })

type EducationFormData = z.infer<typeof educationSchema>

// Project Schema
const projectSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  technologies: z
    .string()
    .nullish()
    .transform((val) => val ?? ''),
  projectUrl: z
    .string()
    .url('Must be a valid URL')
    .or(z.literal(''))
    .nullish()
    .transform((val) => val ?? ''),
  repositoryUrl: z
    .string()
    .url('Must be a valid URL')
    .or(z.literal(''))
    .nullish()
    .transform((val) => val ?? ''),
  startDate: z
    .string()
    .nullish()
    .transform((val) => val ?? ''),
  endDate: z
    .string()
    .nullish()
    .transform((val) => val ?? ''),
  isCurrent: z.boolean(),
})

type ProjectFormData = z.infer<typeof projectSchema>

// Use WorkExperience type from @/types for experiences

interface Education extends EducationFormData {
  id?: string
  candidateId?: string
  isCurrentlyEnrolled?: boolean
  institutionDetails?: {
    id: string
    name: string
  }
  university?: {
    id: string
    name: string
  }
}

interface Project extends ProjectFormData {
  id?: string
}

interface Skill {
  id: string
  skillId: string
  name: string
  proficiency: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'EXPERT'
  category?: string
}

interface Language {
  id?: string
  name: string
  proficiency: 'BASIC' | 'CONVERSATIONAL' | 'FLUENT' | 'NATIVE'
}

type TabType =
  | 'basic'
  | 'experience'
  | 'education'
  | 'projects'
  | 'skills'
  | 'languages'
  | 'documents'
  | 'sharing'

export default function CandidateProfileEdit() {
  const { t } = useTranslation('profile')
  const navigate = useNavigate()
  const location = useLocation()
  const { user, updateUser } = useAuth()
  const [activeTab, setActiveTab] = useState<TabType>('basic')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Experiences
  const [experiences, setExperiences] = useState<WorkExperience[]>([])
  const [isExperienceModalOpen, setIsExperienceModalOpen] = useState(false)
  const [editingExperience, setEditingExperience] = useState<WorkExperience | null>(null)

  // Education
  const [education, setEducation] = useState<Education[]>([])
  const [isEducationModalOpen, setIsEducationModalOpen] = useState(false)
  const [editingEducation, setEditingEducation] = useState<Education | null>(null)

  // Universities and Institutions
  const [universities, setUniversities] = useState<University[]>([])
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [selectedUniversityId, setSelectedUniversityId] = useState<string>('')
  const [isStructuredMode, setIsStructuredMode] = useState(true)
  const [isLoadingInstitutions, setIsLoadingInstitutions] = useState(false)

  // Projects
  const [projects, setProjects] = useState<Project[]>([])
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)

  // Skills
  const [skills, setSkills] = useState<Skill[]>([])
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null)
  const [selectedSkill, setSelectedSkill] = useState<SkillSearchResult | null>(null)
  const [newSkillProficiency, setNewSkillProficiency] = useState<
    'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'EXPERT'
  >('INTERMEDIATE')

  // Languages
  const [languages, setLanguages] = useState<Language[]>([])
  const [newLanguageName, setNewLanguageName] = useState('')
  const [newLanguageProficiency, setNewLanguageProficiency] =
    useState<Language['proficiency']>('CONVERSATIONAL')

  // Documents
  const [cvUrl, setCvUrl] = useState<string | undefined>(undefined)
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | undefined>(undefined)

  // Profile Settings (Sharing & Appearance)
  const [profileSettings, setProfileSettings] = useState<ProfileSettings>(
    getProfileSettings(user?.profileSettings)
  )
  const updateProfileSettingsMutation = useUpdateProfileSettings()

  // Forms
  const basicInfoForm = useForm<BasicInfoFormData>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      jobTitle: '',
      location: '',
      bio: '',
      phone: '',
      linkedIn: '',
      github: '',
      portfolio: '',
    },
  })

  const experienceForm = useForm<ExperienceFormData>({
    resolver: zodResolver(experienceSchema),
    defaultValues: {
      isCurrent: false,
    },
  })

  const educationForm = useForm<EducationFormData>({
    resolver: zodResolver(educationSchema),
  })

  const projectForm = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      isCurrent: false,
    },
  })

  // Helper function to format dates
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'N/A'
    try {
      return format(new Date(dateString), 'MMM yyyy')
    } catch {
      return dateString
    }
  }

  // Sort experiences (current first, then by end date descending)
  const sortedExperiences = [...experiences].sort((a, b) => {
    // Current roles come first
    if (a.isCurrent && !b.isCurrent) return -1
    if (!a.isCurrent && b.isCurrent) return 1

    // For completed roles, sort by end date (most recent first)
    if (!a.isCurrent && !b.isCurrent) {
      const dateA = a.endDate ? new Date(a.endDate).getTime() : 0
      const dateB = b.endDate ? new Date(b.endDate).getTime() : 0
      return dateB - dateA
    }

    // For current roles, sort by start date (most recent first)
    const dateA = new Date(a.startDate).getTime()
    const dateB = new Date(b.startDate).getTime()
    return dateB - dateA
  })

  // Sort education (currently enrolled first, then by end date descending)
  const sortedEducation = [...education].sort((a, b) => {
    // Currently enrolled come first
    if (a.isCurrentlyEnrolled && !b.isCurrentlyEnrolled) return -1
    if (!a.isCurrentlyEnrolled && b.isCurrentlyEnrolled) return 1

    // For completed education, sort by end date (most recent first)
    if (!a.isCurrentlyEnrolled && !b.isCurrentlyEnrolled) {
      const dateA = a.endDate ? new Date(a.endDate).getTime() : 0
      const dateB = b.endDate ? new Date(b.endDate).getTime() : 0
      return dateB - dateA
    }

    // For currently enrolled, sort by start date (most recent first)
    const dateA = new Date(a.startDate).getTime()
    const dateB = new Date(b.startDate).getTime()
    return dateB - dateA
  })

  // Sort projects (current first, then by end date descending)
  const sortedProjects = [...projects].sort((a, b) => {
    // Current projects come first
    if (a.isCurrent && !b.isCurrent) return -1
    if (!a.isCurrent && b.isCurrent) return 1

    // For completed projects, sort by end date (most recent first)
    if (!a.isCurrent && !b.isCurrent) {
      const dateA = a.endDate ? new Date(a.endDate).getTime() : 0
      const dateB = b.endDate ? new Date(b.endDate).getTime() : 0
      return dateB - dateA
    }

    // For current projects, sort by start date (most recent first)
    const dateA = a.startDate ? new Date(a.startDate).getTime() : 0
    const dateB = b.startDate ? new Date(b.startDate).getTime() : 0
    return dateB - dateA
  })

  // Handle hash navigation (for quick links from dashboard)
  useEffect(() => {
    const hash = location.hash.replace('#', '') as TabType
    if (
      hash &&
      [
        'basic',
        'experience',
        'education',
        'projects',
        'skills',
        'languages',
        'documents',
        'sharing',
      ].includes(hash)
    ) {
      setActiveTab(hash)
    }
  }, [location])

  // Function to change tab and update URL hash.
  // Uses React Router's navigate so location stays in sync — using
  // window.history.replaceState directly would leave React Router with a
  // stale location, causing hash-based navigation from the dashboard widget
  // to silently no-op (same URL from React Router's perspective).
  const changeTab = (tab: TabType) => {
    setActiveTab(tab)
    navigate({ hash: `#${tab}` }, { replace: true })
  }

  // Update profile settings when user changes
  useEffect(() => {
    if (user?.profileSettings) {
      setProfileSettings(getProfileSettings(user.profileSettings))
    }
  }, [user?.profileSettings])

  // Fetch profile data - only if user has given consent
  useEffect(() => {
    // Don't fetch profile data until user has given consent
    if (user?.dataProcessingConsent !== true) {
      console.log('[CandidateProfileEdit] User has not given consent - skipping profile fetch')
      setIsLoading(false)
      return
    }

    console.log('[CandidateProfileEdit] User has given consent - fetching profile...')
    fetchProfile()
  }, [user?.dataProcessingConsent])

  // Fetch universities on mount
  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const response = await universityService.getUniversities()
        setUniversities(response.universities || [])
      } catch (error) {
        console.error('Failed to fetch universities:', error)
        // Don't show error toast, universities are optional
      }
    }
    fetchUniversities()
  }, [])

  // Fetch institutions when university is selected
  useEffect(() => {
    const fetchInstitutions = async () => {
      if (!selectedUniversityId) {
        setInstitutions([])
        return
      }

      setIsLoadingInstitutions(true)
      try {
        const response = await universityService.getInstitutionsByUniversity(selectedUniversityId)
        setInstitutions(response.institutions || [])
      } catch (error) {
        console.error('Failed to fetch institutions:', error)
        setInstitutions([])
      } finally {
        setIsLoadingInstitutions(false)
      }
    }

    fetchInstitutions()
  }, [selectedUniversityId])

  const fetchProfile = async () => {
    setIsLoading(true)
    try {
      const response = await api.get('/candidate/profile')
      const profile = response.data

      // Set basic info
      basicInfoForm.reset({
        firstName: profile.firstName || user?.firstName || '',
        lastName: profile.lastName || user?.lastName || '',
        jobTitle: profile.jobTitle || '',
        location: profile.location || '',
        bio: profile.bio || '',
        phone: profile.phone || '',
        linkedIn: profile.linkedIn || '',
        github: profile.github || '',
        portfolio: profile.portfolio || '',
      })

      // Set other data
      setExperiences(profile.experiences || [])
      setEducation(profile.education || [])
      setProjects(profile.projects || [])
      setSkills(profile.skills || [])
      setLanguages(profile.languages || [])
      setCvUrl(profile.cvUrl)
      setProfilePhotoUrl(profile.profilePhotoUrl)

      // Update user in auth store if username or profile settings exist in response
      if (user && (profile.username || profile.profileSettings)) {
        updateUser({
          ...user,
          username: profile.username || user.username,
          profileSettings: profile.profileSettings || user.profileSettings,
        })
      }
    } catch (error: any) {
      console.error('Failed to fetch profile:', error)
      // Don't show error toast, profile might not exist yet
    } finally {
      setIsLoading(false)
    }
  }

  // Basic Info Submit
  const onBasicInfoSubmit = async (data: BasicInfoFormData) => {
    setIsSaving(true)
    try {
      await api.put('/candidate/profile/basic', data)

      // Update user in auth store if name changed
      if (data.firstName !== user?.firstName || data.lastName !== user?.lastName) {
        updateUser({
          ...user!,
          firstName: data.firstName,
          lastName: data.lastName,
        })
      }

      toast.success(t('basicInfo.successMessage'))
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('basicInfo.errorMessage'))
    } finally {
      setIsSaving(false)
    }
  }

  // Experience handlers
  const openAddExperience = () => {
    setEditingExperience(null)
    experienceForm.reset({
      companyName: '',
      jobTitle: '',
      location: '',
      startDate: '',
      endDate: '',
      isCurrent: false,
      description: '',
    })
    setIsExperienceModalOpen(true)
  }

  const openEditExperience = (exp: WorkExperience) => {
    setEditingExperience(exp)
    // API response uses unified field names (companyName, jobTitle, isCurrent)
    experienceForm.reset(exp)
    setIsExperienceModalOpen(true)
  }

  const onExperienceSubmit = async (data: ExperienceFormData) => {
    try {
      if (editingExperience?.id) {
        // Update existing
        await api.put(`/candidate/profile/experience/${editingExperience.id}`, data)
        toast.success(t('experience.updateSuccessMessage'))
      } else {
        // Add new
        await api.post('/candidate/profile/experience', data)
        toast.success(t('experience.addSuccessMessage'))
      }
      setIsExperienceModalOpen(false)
      // Refetch profile to get updated experiences with correct field names
      await fetchProfile()
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('experience.errorMessage'))
    }
  }

  const deleteExperience = async (id: string) => {
    if (!confirm(t('experience.deleteConfirm'))) return

    try {
      await api.delete(`/candidate/profile/experience/${id}`)
      toast.success(t('experience.deleteSuccessMessage'))
      // Refetch profile to get updated experiences and experience level
      await fetchProfile()
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('experience.errorMessage'))
    }
  }

  // Education handlers
  const openAddEducation = () => {
    setEditingEducation(null)
    setIsStructuredMode(true) // Default to structured mode
    setSelectedUniversityId('')
    setInstitutions([])
    educationForm.reset({
      institution: '',
      universityId: '',
      institutionId: '',
      degree: '',
      fieldOfStudy: '',
      startDate: '',
      endDate: '',
      description: '',
    })
    setIsEducationModalOpen(true)
  }

  const openEditEducation = (edu: Education) => {
    setEditingEducation(edu)

    // Determine mode based on existing data
    const hasStructuredData = !!edu.institutionId
    setIsStructuredMode(hasStructuredData)

    // If editing structured data, set university and institution
    if (hasStructuredData && edu.universityId) {
      setSelectedUniversityId(edu.universityId)
      // Institutions will be fetched by the useEffect
    } else {
      setSelectedUniversityId('')
      setInstitutions([])
    }

    educationForm.reset({
      institution: edu.institution || '',
      universityId: edu.universityId || '',
      institutionId: edu.institutionId || '',
      degree: edu.degree,
      fieldOfStudy: edu.fieldOfStudy,
      startDate: edu.startDate,
      endDate: edu.endDate || '',
      description: edu.description || '',
    })
    setIsEducationModalOpen(true)
  }

  const onEducationSubmit = async (data: EducationFormData) => {
    try {
      // Prepare payload based on mode
      const payload: any = {
        degree: data.degree,
        fieldOfStudy: data.fieldOfStudy,
        startDate: data.startDate,
        endDate: data.endDate || null,
        description: data.description || null,
      }

      if (isStructuredMode) {
        // Structured mode: send universityId and institutionId
        payload.universityId = data.universityId || selectedUniversityId
        payload.institutionId = data.institutionId
        payload.institution = null // Clear free-text field
      } else {
        // Free-text mode: send institution text
        payload.institution = data.institution
        payload.universityId = null
        payload.institutionId = null
      }

      if (editingEducation?.id) {
        // Update existing
        const response = await api.put(
          `/candidate/profile/education/${editingEducation.id}`,
          payload
        )
        setEducation(education.map((e) => (e.id === editingEducation.id ? response.data : e)))
        toast.success(t('education.updateSuccessMessage'))
      } else {
        // Add new
        const response = await api.post('/candidate/profile/education', payload)
        setEducation([...education, response.data])
        toast.success(t('education.addSuccessMessage'))
      }
      setIsEducationModalOpen(false)
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('education.errorMessage'))
    }
  }

  const deleteEducation = async (id: string) => {
    if (!confirm(t('education.deleteConfirm'))) return

    try {
      await api.delete(`/candidate/profile/education/${id}`)
      setEducation(education.filter((e) => e.id !== id))
      toast.success(t('education.deleteSuccessMessage'))
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('education.errorMessage'))
    }
  }

  // Project handlers
  const openAddProject = () => {
    setEditingProject(null)
    projectForm.reset({
      title: '',
      description: '',
      technologies: '',
      projectUrl: '',
      repositoryUrl: '',
      startDate: '',
      endDate: '',
      isCurrent: false,
    })
    setIsProjectModalOpen(true)
  }

  const openEditProject = (proj: Project) => {
    setEditingProject(proj)
    projectForm.reset(proj)
    setIsProjectModalOpen(true)
  }

  const onProjectSubmit = async (data: ProjectFormData) => {
    try {
      if (editingProject?.id) {
        // Update existing
        const response = await api.put(`/candidates/projects/${editingProject.id}`, data)
        setProjects(projects.map((p) => (p.id === editingProject.id ? response.data.project : p)))
        toast.success(t('projects.updateSuccessMessage'))
      } else {
        // Add new
        const response = await api.post('/candidates/projects', data)
        setProjects([...projects, response.data.project])
        toast.success(t('projects.addSuccessMessage'))
      }
      setIsProjectModalOpen(false)
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('projects.errorMessage'))
    }
  }

  const deleteProject = async (id: string) => {
    if (!confirm(t('projects.deleteConfirm'))) return

    try {
      await api.delete(`/candidates/projects/${id}`)
      setProjects(projects.filter((p) => p.id !== id))
      toast.success(t('projects.deleteSuccessMessage'))
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('projects.errorMessage'))
    }
  }

  // Skills handlers
  const handleSkillSelect = (skill: SkillSearchResult) => {
    // Just set the selected skill, don't add it yet
    setSelectedSkill(skill)
  }

  const addSkill = async () => {
    if (!selectedSkill) {
      toast.error(t('skills.errorEmptyName'))
      return
    }

    // Check if user has reached max skills (50)
    if (skills.length >= 50) {
      toast.error(
        'Maximum skill limit reached. You can add a maximum of 50 skills. Please remove some skills before adding new ones.'
      )
      return
    }

    try {
      const response = await api.post('/candidate/profile/skill', {
        skillId: selectedSkill.id,
        proficiency: newSkillProficiency,
      })

      // Add the new skill to the list using the response data
      setSkills([
        ...skills,
        {
          id: response.data.id,
          skillId: response.data.skillId,
          name: response.data.skillName,
          proficiency: response.data.proficiency,
          category: response.data.skillCategory,
        },
      ])

      // Clear selection after successful add
      setSelectedSkill(null)
      setNewSkillProficiency('INTERMEDIATE')
      toast.success(t('skills.addSuccessMessage'))
    } catch (error: any) {
      // Handle specific error cases
      const errorMessage = error.response?.data?.error
      const errorData = error.response?.data

      if (error.response?.status === 400 && errorData?.currentSkillCount !== undefined) {
        // Maximum skill limit reached
        const message =
          errorData.message ||
          `Maximum skill limit reached. You can add a maximum of ${errorData.maxSkills || 50} skills. Please remove some skills before adding new ones.`
        toast.error(message)
      } else if (error.response?.status === 409) {
        // Skill already added
        toast.error(errorMessage || 'This skill is already in your profile')
      } else if (error.response?.status === 404) {
        // Skill not found (shouldn't happen with autocomplete)
        toast.error(errorMessage || 'Skill not found. Please select from the list.')
      } else {
        // Generic error
        toast.error(errorMessage || t('skills.errorMessage'))
      }
    }
  }

  const updateSkillProficiency = async (
    skillId: string,
    newProficiency: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'EXPERT'
  ) => {
    try {
      const response = await api.patch(`/candidate/profile/skill/${skillId}`, {
        proficiency: newProficiency,
      })

      // Update the skill in the local state using response data
      setSkills(
        skills.map((s) =>
          s.id === skillId
            ? {
                ...s,
                proficiency: response.data.skill.proficiency,
                name: response.data.skill.skillName,
                category: response.data.skill.skillCategory,
              }
            : s
        )
      )

      setEditingSkillId(null)
      toast.success(response.data.message || 'Skill proficiency updated successfully')
    } catch (error: any) {
      const errorMessage = error.response?.data?.error

      if (error.response?.status === 404) {
        toast.error(errorMessage || 'Skill not found in your profile')
      } else {
        toast.error(errorMessage || 'Failed to update skill proficiency')
      }
    }
  }

  const deleteSkill = async (id: string) => {
    try {
      await api.delete(`/candidate/profile/skill/${id}`)
      setSkills(skills.filter((s) => s.id !== id))
      toast.success(t('skills.deleteSuccessMessage'))
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('skills.errorMessage'))
    }
  }

  // Languages handlers
  const addLanguage = async () => {
    if (!newLanguageName.trim()) {
      toast.error(t('languages.errorEmptyName'))
      return
    }

    try {
      const response = await api.post('/candidate/profile/language', {
        name: newLanguageName,
        proficiency: newLanguageProficiency,
      })
      setLanguages([...languages, response.data])
      setNewLanguageName('')
      setNewLanguageProficiency('CONVERSATIONAL')
      toast.success(t('languages.addSuccessMessage'))
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('languages.errorMessage'))
    }
  }

  const deleteLanguage = async (id: string) => {
    try {
      await api.delete(`/candidate/profile/language/${id}`)
      setLanguages(languages.filter((l) => l.id !== id))
      toast.success(t('languages.deleteSuccessMessage'))
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('languages.errorMessage'))
    }
  }

  // File upload handlers
  const uploadCV = async (file: File) => {
    const formData = new FormData()
    formData.append('cv', file)

    try {
      const response = await api.post('/candidate/profile/cv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      setCvUrl(response.data.cvUrl)
    } catch (error: any) {
      throw error
    }
  }

  const deleteCV = async () => {
    try {
      await api.delete('/candidate/profile/cv')
      setCvUrl(undefined)
    } catch (error: any) {
      throw error
    }
  }

  const uploadProfilePhoto = async (file: File) => {
    const formData = new FormData()
    formData.append('photo', file)

    try {
      const response = await api.post('/candidate/profile/photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      setProfilePhotoUrl(response.data.photoUrl)

      // Update user in auth store
      if (user) {
        updateUser({
          ...user,
          profilePhoto: response.data.photoUrl,
        })
      }
    } catch (error: any) {
      throw error
    }
  }

  const photoInputRef = useRef<HTMLInputElement>(null)

  const handlePhotoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) await uploadProfilePhoto(file)
    e.target.value = ''
  }

  const deleteProfilePhoto = async () => {
    try {
      await api.delete('/candidate/profile/photo')
      setProfilePhotoUrl(undefined)

      // Update user in auth store
      if (user) {
        updateUser({
          ...user,
          profilePhoto: undefined,
        })
      }
    } catch (error: any) {
      throw error
    }
  }

  const handleThemeChange = (theme: typeof profileSettings.theme) => {
    const newSettings = { ...profileSettings, theme }
    setProfileSettings(newSettings)
    // Attempt to save, but don't block UI if it fails
    updateProfileSettingsMutation.mutate(newSettings)
  }

  const handleColorSchemeChange = (colorScheme: typeof profileSettings.colorScheme) => {
    const newSettings = { ...profileSettings, colorScheme }
    setProfileSettings(newSettings)
    updateProfileSettingsMutation.mutate(newSettings)
  }

  const handleVisibilityChange = (visibility: typeof profileSettings.visibility) => {
    const newSettings = { ...profileSettings, visibility }
    setProfileSettings(newSettings)
    updateProfileSettingsMutation.mutate(newSettings)
  }

  const handleCustomizationChange = (customization: typeof profileSettings.customization) => {
    const newSettings = { ...profileSettings, customization }
    setProfileSettings(newSettings)
    updateProfileSettingsMutation.mutate(newSettings)
  }

  const contentTabs = [
    { id: 'basic' as TabType, label: t('tabs.basicInfo'), icon: User },
    { id: 'experience' as TabType, label: t('tabs.experience'), icon: Briefcase },
    { id: 'education' as TabType, label: t('tabs.education'), icon: GraduationCap },
    { id: 'projects' as TabType, label: t('tabs.projects'), icon: FolderGit2 },
    { id: 'skills' as TabType, label: t('tabs.skills'), icon: Target },
    { id: 'languages' as TabType, label: t('tabs.languages'), icon: Languages },
    { id: 'documents' as TabType, label: t('tabs.documents'), icon: FileText },
  ]

  const getProficiencyColor = (proficiency: string) => {
    switch (proficiency) {
      case 'EXPERT':
        return 'bg-orange-100 text-orange-700 border-orange-300'
      case 'ADVANCED':
      case 'FLUENT':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300'
      case 'INTERMEDIATE':
      case 'CONVERSATIONAL':
        return 'bg-blue-100 text-blue-700 border-blue-300'
      case 'BEGINNER':
      case 'BASIC':
        return 'bg-green-100 text-green-700 border-green-300'
      case 'NATIVE':
        return 'bg-purple-100 text-purple-700 border-purple-300'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300'
    }
  }

  if (isLoading) {
    return (
      <div className="container-custom py-12 flex items-center justify-center min-h-[400px]">
        <div className="card p-8 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-gray-200 border-t-blue-500 rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">{t('edit.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container-custom py-12 space-y-8">
      {/* Header */}
      <div className="animate-slide-down space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{t('edit.title')}</h1>
            <p className="text-gray-600 text-base md:text-lg">{t('edit.subtitle')}</p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/candidate/dashboard')}
            className="hidden md:flex"
          >
            {t('edit.backToDashboard')}
          </Button>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => changeTab('sharing')}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all flex-1 sm:flex-initial ${
              activeTab === 'sharing'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-700 border border-gray-200'
            }`}
          >
            <Palette className="w-5 h-5" />
            <span>{t('edit.sharingAppearance')}</span>
          </button>
          <Button
            variant="outline"
            onClick={() => navigate('/candidate/dashboard')}
            className="flex md:hidden flex-1 sm:flex-initial"
            fullWidth={false}
          >
            {t('edit.backToDashboard')}
          </Button>
        </div>
      </div>

      {/* Profile Photo */}
      {activeTab !== 'sharing' && (
        <div className="flex items-center gap-4">
          {profilePhotoUrl ? (
            <img
              src={profilePhotoUrl}
              alt="Profile"
              className="w-16 h-16 rounded-full rounded-avatar object-cover border-2 border-gray-200 shadow-sm"
            />
          ) : (
            <div className="w-16 h-16 rounded-full rounded-avatar bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
              <User className="w-7 h-7 text-gray-400" />
            </div>
          )}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png"
            className="hidden"
            onChange={handlePhotoFileChange}
          />
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {profilePhotoUrl
              ? t('documents.photo.replaceButton')
              : t('documents.photo.uploadButton')}
          </button>
          {profilePhotoUrl && (
            <button
              type="button"
              onClick={deleteProfilePhoto}
              className="text-sm text-red-500 hover:text-red-600"
            >
              {t('documents.photo.deleteButton')}
            </button>
          )}
        </div>
      )}

      {/* Content Tabs */}
      {activeTab !== 'sharing' && (
        <div className="card animate-slide-up">
          {/* Mobile: Dropdown Menu */}
          <div className="md:hidden p-3">
            <label htmlFor="tab-select" className="sr-only">
              {t('edit.selectSection')}
            </label>
            <div className="relative">
              <select
                id="tab-select"
                value={activeTab}
                onChange={(e) => changeTab(e.target.value as TabType)}
                className="w-full appearance-none px-4 py-3 pr-10 rounded-xl border-2 border-gray-200 bg-white text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                {contentTabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {tab.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Desktop: Tab Buttons */}
          <div className="hidden md:flex gap-2 p-2 flex-wrap">
            {contentTabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => changeTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="animate-slide-up delay-100">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <Card padding="lg" className="shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <User className="w-6 h-6 text-blue-600" />
              {t('basicInfo.title')}
            </h2>

            <form onSubmit={basicInfoForm.handleSubmit(onBasicInfoSubmit)} className="space-y-6">
              {/* Name Fields */}
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  {...basicInfoForm.register('firstName')}
                  label={t('basicInfo.firstName.label')}
                  placeholder={t('basicInfo.firstName.placeholder')}
                  error={basicInfoForm.formState.errors.firstName?.message}
                  icon={<User className="w-5 h-5" />}
                  disabled={isSaving}
                />
                <Input
                  {...basicInfoForm.register('lastName')}
                  label={t('basicInfo.lastName.label')}
                  placeholder={t('basicInfo.lastName.placeholder')}
                  error={basicInfoForm.formState.errors.lastName?.message}
                  icon={<User className="w-5 h-5" />}
                  disabled={isSaving}
                />
              </div>

              {/* Job Title & Location */}
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  {...basicInfoForm.register('jobTitle')}
                  label={t('basicInfo.jobTitle.label')}
                  placeholder={t('basicInfo.jobTitle.placeholder')}
                  error={basicInfoForm.formState.errors.jobTitle?.message}
                  icon={<Briefcase className="w-5 h-5" />}
                  disabled={isSaving}
                />
                <Input
                  {...basicInfoForm.register('location')}
                  label={t('basicInfo.location.label')}
                  placeholder={t('basicInfo.location.placeholder')}
                  error={basicInfoForm.formState.errors.location?.message}
                  icon={<MapPin className="w-5 h-5" />}
                  disabled={isSaving}
                />
              </div>

              {/* Bio */}
              <Textarea
                {...basicInfoForm.register('bio')}
                label={t('basicInfo.bio.label')}
                placeholder={t('basicInfo.bio.placeholder')}
                error={basicInfoForm.formState.errors.bio?.message}
                helperText={t('basicInfo.bio.helperText', {
                  count: basicInfoForm.watch('bio')?.length || 0,
                })}
                disabled={isSaving}
              />

              {/* Contact & Links */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('basicInfo.contactLinks')}
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    {...basicInfoForm.register('phone')}
                    label={t('basicInfo.phone.label')}
                    placeholder={t('basicInfo.phone.placeholder')}
                    error={basicInfoForm.formState.errors.phone?.message}
                    icon={<Phone className="w-5 h-5" />}
                    disabled={isSaving}
                  />
                  <Input
                    {...basicInfoForm.register('linkedIn')}
                    label={t('basicInfo.linkedIn.label')}
                    placeholder={t('basicInfo.linkedIn.placeholder')}
                    error={basicInfoForm.formState.errors.linkedIn?.message}
                    icon={<LinkIcon className="w-5 h-5" />}
                    disabled={isSaving}
                  />
                  <Input
                    {...basicInfoForm.register('github')}
                    label={t('basicInfo.github.label')}
                    placeholder={t('basicInfo.github.placeholder')}
                    error={basicInfoForm.formState.errors.github?.message}
                    icon={<LinkIcon className="w-5 h-5" />}
                    disabled={isSaving}
                  />
                  <Input
                    {...basicInfoForm.register('portfolio')}
                    label={t('basicInfo.portfolio.label')}
                    placeholder={t('basicInfo.portfolio.placeholder')}
                    error={basicInfoForm.formState.errors.portfolio?.message}
                    icon={<LinkIcon className="w-5 h-5" />}
                    disabled={isSaving}
                  />
                </div>
              </div>

              <Button type="submit" variant="primary" isLoading={isSaving} className="group">
                <Save className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                {t('basicInfo.saveButton')}
              </Button>
            </form>
          </Card>
        )}

        {/* Experience Tab */}
        {activeTab === 'experience' && (
          <Card padding="lg" className="shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Briefcase className="w-6 h-6 text-blue-600" />
                {t('experience.title')}
              </h2>
              <Button variant="primary" onClick={openAddExperience}>
                <Plus className="w-5 h-5 md:mr-2" />
                <span className="hidden md:inline">{t('experience.addButton')}</span>
              </Button>
            </div>

            {experiences.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{t('experience.noExperience')}</p>
                <Button variant="outline" onClick={openAddExperience}>
                  {t('experience.addButton')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {sortedExperiences.map((exp) => (
                  <div
                    key={exp.id}
                    className="card p-6 hover:bg-gray-50 transition-all border border-gray-200"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900">{exp.jobTitle}</h3>
                        <p className="text-blue-600 font-medium">{exp.companyName}</p>
                        {exp.location && (
                          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <MapPin className="w-4 h-4" />
                            {exp.location}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditExperience(exp)}
                          className="p-2 rounded-lg hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => exp.id && deleteExperience(exp.id)}
                          className="p-2 rounded-lg hover:bg-red-50 text-gray-600 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 flex items-center gap-2 mb-3">
                      <Calendar className="w-4 h-4" />
                      {formatDate(exp.startDate)} -{' '}
                      {exp.isCurrent ? t('experience.present') : formatDate(exp.endDate)}
                      {exp.isCurrent && (
                        <span className="px-2 py-1 rounded-lg bg-green-100 text-green-700 text-xs font-medium">
                          {t('experience.current')}
                        </span>
                      )}
                    </p>
                    {exp.description && (
                      <p className="text-gray-700 text-sm whitespace-pre-line">{exp.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Education Tab */}
        {activeTab === 'education' && (
          <Card padding="lg" className="shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <GraduationCap className="w-6 h-6 text-blue-600" />
                {t('education.title')}
              </h2>
              <Button variant="primary" onClick={openAddEducation}>
                <Plus className="w-5 h-5 md:mr-2" />
                <span className="hidden md:inline">{t('education.addButton')}</span>
              </Button>
            </div>

            {education.length === 0 ? (
              <div className="text-center py-12">
                <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{t('education.noEducation')}</p>
                <Button variant="outline" onClick={openAddEducation}>
                  {t('education.addButton')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {sortedEducation.map((edu) => {
                  // Get institution name (structured or legacy)
                  const institutionName = edu.institutionDetails?.name
                    ? edu.institutionDetails.name
                    : edu.institution || 'Unknown Institution'

                  // Get university name if available
                  const universityName = edu.university?.name

                  return (
                    <div
                      key={edu.id}
                      className="card p-6 hover:bg-gray-50 transition-all border border-gray-200"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-gray-900">
                            {edu.degree} in {edu.fieldOfStudy}
                          </h3>
                          <p className="text-blue-600 font-medium">{institutionName}</p>
                          {universityName && institutionName !== universityName && (
                            <p className="text-sm text-gray-600">{universityName}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditEducation(edu)}
                            className="p-2 rounded-lg hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => edu.id && deleteEducation(edu.id)}
                            className="p-2 rounded-lg hover:bg-red-50 text-gray-600 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 flex items-center gap-2 mb-3">
                        <Calendar className="w-4 h-4" />
                        {formatDate(edu.startDate)} -{' '}
                        {edu.isCurrentlyEnrolled ? 'Present' : formatDate(edu.endDate)}
                      </p>
                      {edu.description && (
                        <p className="text-gray-700 text-sm whitespace-pre-line">
                          {edu.description}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <Card padding="lg" className="shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <FolderGit2 className="w-6 h-6 text-blue-600" />
                {t('projects.title')}
              </h2>
              <Button variant="primary" onClick={openAddProject}>
                <Plus className="w-5 h-5 md:mr-2" />
                <span className="hidden md:inline">{t('projects.addButton')}</span>
              </Button>
            </div>

            {projects.length === 0 ? (
              <div className="text-center py-12">
                <FolderGit2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{t('projects.noProjects')}</p>
                <Button variant="outline" onClick={openAddProject}>
                  {t('projects.addButton')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {sortedProjects.map((project) => (
                  <div
                    key={project.id}
                    className="card p-6 hover:bg-gray-50 transition-all border border-gray-200"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-semibold text-gray-900">{project.title}</h3>
                          {project.isCurrent && (
                            <span className="px-2 py-1 rounded-lg bg-green-100 text-green-700 text-xs font-medium">
                              {t('projects.current')}
                            </span>
                          )}
                        </div>
                        {project.technologies && (
                          <p className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">{t('projects.technologies')}:</span>{' '}
                            {project.technologies}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditProject(project)}
                          className="p-2 rounded-lg hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => project.id && deleteProject(project.id)}
                          className="p-2 rounded-lg hover:bg-red-50 text-gray-600 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    {(project.startDate || project.endDate) && (
                      <p className="text-sm text-gray-600 flex items-center gap-2 mb-3">
                        <Calendar className="w-4 h-4" />
                        {formatDate(project.startDate)} -{' '}
                        {project.isCurrent ? t('experience.present') : formatDate(project.endDate)}
                      </p>
                    )}
                    {project.description && (
                      <p className="text-gray-700 text-sm whitespace-pre-line mb-3">
                        {project.description}
                      </p>
                    )}
                    <div className="flex gap-3">
                      {project.projectUrl && (
                        <a
                          href={project.projectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {t('projects.liveDemo')}
                        </a>
                      )}
                      {project.repositoryUrl && (
                        <a
                          href={project.repositoryUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <Github className="w-4 h-4" />
                          {t('projects.repository')}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Skills Tab */}
        {activeTab === 'skills' && (
          <Card padding="lg" className="shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Target className="w-6 h-6 text-blue-600" />
                {t('skills.title')}
              </h2>
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm font-medium ${skills.length >= 50 ? 'text-red-600' : 'text-gray-600'}`}
                >
                  {skills.length} / 50
                </span>
                {skills.length >= 50 && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    Max reached
                  </span>
                )}
              </div>
            </div>

            {/* Add Skill */}
            <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 mb-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('skills.addButton')}</h3>

              <div className="flex flex-col sm:flex-row gap-4">
                {/* Skill Search - Left side */}
                <div className="flex-1">
                  <SkillAutocomplete
                    label={t('skills.skillName.label')}
                    placeholder={
                      skills.length >= 50
                        ? 'Maximum skill limit reached'
                        : t('skills.skillName.placeholder')
                    }
                    onSelect={handleSkillSelect}
                    selectedSkills={skills.map((s) => s.skillId)}
                    disabled={skills.length >= 50}
                  />
                </div>

                {/* Proficiency Selector - Right side */}
                <div className="sm:w-48">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('skills.proficiency.label')}
                  </label>
                  <select
                    value={newSkillProficiency}
                    onChange={(e) =>
                      setNewSkillProficiency(
                        e.target.value as 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'EXPERT'
                      )
                    }
                    disabled={skills.length >= 50}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="BEGINNER">{t('skills.proficiency.beginner')}</option>
                    <option value="INTERMEDIATE">{t('skills.proficiency.intermediate')}</option>
                    <option value="ADVANCED">{t('skills.proficiency.advanced')}</option>
                    <option value="EXPERT">{t('skills.proficiency.expert')}</option>
                  </select>
                </div>
              </div>

              {/* Selected skill preview and Add button */}
              {selectedSkill && (
                <div className="mt-4 flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg flex-1">
                    <span className="text-sm text-gray-600">Selected:</span>
                    <span className="font-medium text-gray-900">{selectedSkill.name}</span>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ml-2 bg-${selectedSkill.category === 'framework' ? 'purple' : 'blue'}-100 text-${selectedSkill.category === 'framework' ? 'purple' : 'blue'}-700`}
                    >
                      {selectedSkill.category}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedSkill(null)}
                      className="ml-auto text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <Button
                    variant="primary"
                    onClick={addSkill}
                    disabled={skills.length >= 50}
                    className="shrink-0"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    {t('skills.addButton')}
                  </Button>
                </div>
              )}

              {/* Warning messages */}
              {skills.length >= 50 ? (
                <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700">
                    <p className="font-medium">Maximum skill limit reached</p>
                    <p className="mt-1">
                      You can add a maximum of 50 skills to your profile. Please remove some skills
                      before adding new ones.
                    </p>
                  </div>
                </div>
              ) : skills.length >= 45 ? (
                <div className="mt-3 flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-700">
                    You're approaching the maximum skill limit ({skills.length}/50). Consider
                    keeping only your most relevant skills.
                  </p>
                </div>
              ) : (
                <p className="mt-3 text-sm text-gray-500">
                  Search and select a skill, choose proficiency level, then click Add. You can add
                  up to 50 skills. You can also edit proficiency later by clicking on it in the
                  skill tag.
                </p>
              )}
            </div>

            {/* Skills List */}
            {skills.length === 0 ? (
              <div className="text-center py-12">
                <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">{t('skills.noSkills')}</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {skills.map((skill) => (
                  <div
                    key={skill.id}
                    className={`group flex items-center gap-3 px-4 py-2 rounded-xl border ${getProficiencyColor(skill.proficiency)}`}
                  >
                    <span className="font-medium">{skill.name}</span>
                    {editingSkillId === skill.id ? (
                      <select
                        value={skill.proficiency}
                        onChange={(e) =>
                          updateSkillProficiency(skill.id!, e.target.value as Skill['proficiency'])
                        }
                        onBlur={() => setEditingSkillId(null)}
                        autoFocus
                        className="text-xs px-2 py-1 rounded border border-gray-300 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="BEGINNER">{t('skills.proficiency.beginner')}</option>
                        <option value="INTERMEDIATE">{t('skills.proficiency.intermediate')}</option>
                        <option value="ADVANCED">{t('skills.proficiency.advanced')}</option>
                        <option value="EXPERT">{t('skills.proficiency.expert')}</option>
                      </select>
                    ) : (
                      <button
                        onClick={() => setEditingSkillId(skill.id!)}
                        className="flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-transparent hover:border-gray-400 hover:bg-white/50 transition-all cursor-pointer group/edit"
                        title="Click to edit proficiency level"
                      >
                        <span className="font-medium">
                          {t(`skills.proficiency.${skill.proficiency.toLowerCase()}`)}
                        </span>
                        <Edit2 className="w-3 h-3 opacity-0 group-hover/edit:opacity-100 transition-opacity" />
                      </button>
                    )}
                    <button
                      onClick={() => skill.id && deleteSkill(skill.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity ml-auto hover:scale-110"
                      title="Remove skill"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Helper text */}
            {skills.length > 0 && (
              <div className="mt-4 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700">
                  <span className="font-medium">Tip:</span> Click on any proficiency level (e.g.,
                  Intermediate, Advanced) to change it. Hover over skills to see the edit and delete
                  options.
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Languages Tab */}
        {activeTab === 'languages' && (
          <Card padding="lg" className="shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <Languages className="w-6 h-6 text-blue-600" />
              {t('languages.title')}
            </h2>

            {/* Add Language */}
            <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 mb-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('languages.addButton')}
              </h3>
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <Input
                    label={t('languages.languageName.label')}
                    placeholder={t('languages.languageName.placeholder')}
                    value={newLanguageName}
                    onChange={(e) => setNewLanguageName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addLanguage()}
                  />
                </div>
                <div className="min-w-[180px]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('languages.proficiency.label')}
                  </label>
                  <select
                    value={newLanguageProficiency}
                    onChange={(e) =>
                      setNewLanguageProficiency(e.target.value as Language['proficiency'])
                    }
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="BASIC">{t('languages.proficiency.basic')}</option>
                    <option value="CONVERSATIONAL">
                      {t('languages.proficiency.conversational')}
                    </option>
                    <option value="FLUENT">{t('languages.proficiency.fluent')}</option>
                    <option value="NATIVE">{t('languages.proficiency.native')}</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button variant="primary" onClick={addLanguage}>
                    <Plus className="w-5 h-5 md:mr-2" />
                    <span className="hidden md:inline">{t('languages.addButton')}</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Languages List */}
            {languages.length === 0 ? (
              <div className="text-center py-12">
                <Languages className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">{t('languages.noLanguages')}</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {languages.map((language) => (
                  <div
                    key={language.id}
                    className={`group flex items-center gap-3 px-4 py-2 rounded-xl border ${getProficiencyColor(language.proficiency)}`}
                  >
                    <span className="font-medium">{language.name}</span>
                    <span className="text-xs opacity-70">
                      {t(`languages.proficiency.${language.proficiency.toLowerCase()}`)}
                    </span>
                    <button
                      onClick={() => language.id && deleteLanguage(language.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity ml-2 hover:scale-110"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="space-y-6">
            <Card className="card p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <FileText className="w-6 h-6 text-blue-600" />
                {t('documents.title')}
              </h2>
            </Card>

            {/* CV Upload */}
            <FileUpload
              label={t('documents.cv.label')}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              maxSize={5}
              currentFile={cvUrl}
              onUpload={uploadCV}
              onDelete={cvUrl ? deleteCV : undefined}
              type="document"
              helperText={t('documents.cv.helperText')}
            />
          </div>
        )}

        {/* Sharing & Appearance Tab */}
        {activeTab === 'sharing' && (
          <div className="space-y-6">
            <Card className="card p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Palette className="w-6 h-6 text-blue-600" />
                {t('sharing.title')}
              </h2>
            </Card>

            {/* Username Selector */}
            <Card className="card p-8">
              <UsernameSelector
                currentUsername={user?.username}
                firstName={user?.firstName}
                lastName={user?.lastName}
              />
            </Card>

            {/* Theme Selector */}
            <Card className="card p-8">
              <ThemeSelector currentTheme={profileSettings.theme} onChange={handleThemeChange} />
            </Card>

            {/* Color Scheme Selector */}
            <Card className="card p-8">
              <ColorSchemeSelector
                currentScheme={profileSettings.colorScheme}
                onChange={handleColorSchemeChange}
              />
            </Card>

            {/* Accessibility Settings */}
            <Card className="card p-8">
              <AccessibilitySettings
                customization={profileSettings.customization}
                onChange={handleCustomizationChange}
              />
            </Card>

            {/* Privacy Controls */}
            <Card className="card p-8">
              <PrivacyControls
                visibility={profileSettings.visibility}
                onChange={handleVisibilityChange}
              />
            </Card>

            {/* Share Tools */}
            <Card className="card p-8">
              <ShareTools username={user?.username} />
            </Card>

            {/* Delete Account Section - Danger Zone */}
            <DeleteAccountSection />
          </div>
        )}
      </div>

      {/* Experience Modal */}
      <Modal
        isOpen={isExperienceModalOpen}
        onClose={() => setIsExperienceModalOpen(false)}
        title={editingExperience ? t('experience.form.editTitle') : t('experience.form.addTitle')}
        size="lg"
      >
        <form onSubmit={experienceForm.handleSubmit(onExperienceSubmit)} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Input
              {...experienceForm.register('companyName')}
              label={t('experience.form.company.label')}
              placeholder={t('experience.form.company.placeholder')}
              error={experienceForm.formState.errors.companyName?.message}
              icon={<Building className="w-5 h-5" />}
            />
            <Input
              {...experienceForm.register('jobTitle')}
              label={t('experience.form.jobTitle.label')}
              placeholder={t('experience.form.jobTitle.placeholder')}
              error={experienceForm.formState.errors.jobTitle?.message}
              icon={<Briefcase className="w-5 h-5" />}
            />
          </div>

          <Input
            {...experienceForm.register('location')}
            label={t('experience.form.location.label')}
            placeholder={t('experience.form.location.placeholder')}
            error={experienceForm.formState.errors.location?.message}
            icon={<MapPin className="w-5 h-5" />}
          />

          <div className="grid md:grid-cols-2 gap-6">
            <Input
              {...experienceForm.register('startDate')}
              label={t('experience.form.startDate.label')}
              type="month"
              placeholder={t('experience.form.startDate.placeholder')}
              error={experienceForm.formState.errors.startDate?.message}
              icon={<Calendar className="w-5 h-5" />}
            />
            <Input
              {...experienceForm.register('endDate')}
              label={t('experience.form.endDate.label')}
              type="month"
              placeholder={t('experience.form.endDate.placeholder')}
              error={experienceForm.formState.errors.endDate?.message}
              icon={<Calendar className="w-5 h-5" />}
              disabled={experienceForm.watch('isCurrent')}
            />
          </div>

          <label className="flex items-center gap-3 text-gray-700 cursor-pointer hover:text-gray-900 transition-colors">
            <input
              type="checkbox"
              {...experienceForm.register('isCurrent')}
              className="w-4 h-4 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500"
            />
            {t('experience.form.isCurrent')}
          </label>

          <Textarea
            {...experienceForm.register('description')}
            label={t('experience.form.description.label')}
            placeholder={t('experience.form.description.placeholder')}
            error={experienceForm.formState.errors.description?.message}
          />

          <div className="flex gap-3 justify-end">
            <Button variant="glass" type="button" onClick={() => setIsExperienceModalOpen(false)}>
              {t('experience.form.cancelButton')}
            </Button>
            <Button variant="primary" type="submit">
              <Save className="w-5 h-5 mr-2" />
              {t('experience.form.saveButton')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Education Modal */}
      <Modal
        isOpen={isEducationModalOpen}
        onClose={() => setIsEducationModalOpen(false)}
        title={editingEducation ? t('education.form.editTitle') : t('education.form.addTitle')}
        size="lg"
      >
        <form onSubmit={educationForm.handleSubmit(onEducationSubmit)} className="space-y-6">
          {/* Mode Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
            <span className="text-sm font-medium text-gray-700">
              {isStructuredMode ? 'Select from list' : 'Free text input'}
            </span>
            <button
              type="button"
              onClick={() => {
                setIsStructuredMode(!isStructuredMode)
                // Clear fields when switching modes
                if (isStructuredMode) {
                  // Switching to free-text mode
                  educationForm.setValue('universityId', '')
                  educationForm.setValue('institutionId', '')
                  setSelectedUniversityId('')
                } else {
                  // Switching to structured mode
                  educationForm.setValue('institution', '')
                }
              }}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium underline"
            >
              {isStructuredMode ? 'My institution is not listed' : 'Select from list'}
            </button>
          </div>

          {/* Structured Mode: University and Institution Dropdowns */}
          {isStructuredMode ? (
            <div className="space-y-4">
              {/* University Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  University <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedUniversityId}
                  onChange={(e) => {
                    const universityId = e.target.value
                    setSelectedUniversityId(universityId)
                    educationForm.setValue('universityId', universityId)
                    educationForm.setValue('institutionId', '') // Clear institution when university changes
                  }}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a university</option>
                  {universities.map((university) => (
                    <option key={university.id} value={university.id}>
                      {university.name}
                      {university.location && ` - ${university.location}`}
                    </option>
                  ))}
                </select>
                {educationForm.formState.errors.institutionId && !selectedUniversityId && (
                  <p className="mt-1 text-sm text-red-600">University selection is required</p>
                )}
              </div>

              {/* Institution Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Institution <span className="text-red-500">*</span>
                </label>
                <select
                  {...educationForm.register('institutionId')}
                  disabled={!selectedUniversityId || isLoadingInstitutions}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {isLoadingInstitutions
                      ? 'Loading institutions...'
                      : !selectedUniversityId
                        ? 'Select a university first'
                        : 'Select an institution'}
                  </option>
                  {institutions.map((institution) => (
                    <option key={institution.id} value={institution.id}>
                      {institution.abbreviation
                        ? `${institution.abbreviation} - ${institution.name}`
                        : institution.name}
                    </option>
                  ))}
                </select>
                {educationForm.formState.errors.institutionId && (
                  <p className="mt-1 text-sm text-red-600">
                    {educationForm.formState.errors.institutionId.message}
                  </p>
                )}
              </div>
            </div>
          ) : (
            /* Free-Text Mode: Institution Text Input */
            <Input
              {...educationForm.register('institution')}
              label="Institution"
              placeholder="e.g., University of Tunis"
              error={educationForm.formState.errors.institution?.message}
              icon={<GraduationCap className="w-5 h-5" />}
            />
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <Input
              {...educationForm.register('degree')}
              label={t('education.form.degree.label')}
              placeholder={t('education.form.degree.placeholder')}
              error={educationForm.formState.errors.degree?.message}
            />
            <Input
              {...educationForm.register('fieldOfStudy')}
              label={t('education.form.fieldOfStudy.label')}
              placeholder={t('education.form.fieldOfStudy.placeholder')}
              error={educationForm.formState.errors.fieldOfStudy?.message}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Input
              {...educationForm.register('startDate')}
              label={t('education.form.startDate.label')}
              type="month"
              placeholder={t('education.form.startDate.placeholder')}
              error={educationForm.formState.errors.startDate?.message}
              icon={<Calendar className="w-5 h-5" />}
            />
            <Input
              {...educationForm.register('endDate')}
              label={t('education.form.endDate.label')}
              type="month"
              placeholder={t('education.form.endDate.placeholder')}
              error={educationForm.formState.errors.endDate?.message}
              icon={<Calendar className="w-5 h-5" />}
            />
          </div>

          <Textarea
            {...educationForm.register('description')}
            label={t('education.form.description.label')}
            placeholder={t('education.form.description.placeholder')}
            error={educationForm.formState.errors.description?.message}
          />

          <div className="flex gap-3 justify-end">
            <Button variant="glass" type="button" onClick={() => setIsEducationModalOpen(false)}>
              {t('education.form.cancelButton')}
            </Button>
            <Button variant="primary" type="submit">
              <Save className="w-5 h-5 mr-2" />
              {t('education.form.saveButton')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Project Modal */}
      <Modal
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        title={editingProject ? t('projects.form.editTitle') : t('projects.form.addTitle')}
        size="lg"
      >
        <form onSubmit={projectForm.handleSubmit(onProjectSubmit)} className="space-y-6">
          <Input
            {...projectForm.register('title')}
            label={t('projects.form.projectTitle.label')}
            placeholder={t('projects.form.projectTitle.placeholder')}
            error={projectForm.formState.errors.title?.message}
            icon={<FolderGit2 className="w-5 h-5" />}
          />

          <Textarea
            {...projectForm.register('description')}
            label={t('projects.form.description.label')}
            placeholder={t('projects.form.description.placeholder')}
            error={projectForm.formState.errors.description?.message}
          />

          <Input
            {...projectForm.register('technologies')}
            label={t('projects.form.technologies.label')}
            placeholder={t('projects.form.technologies.placeholder')}
            error={projectForm.formState.errors.technologies?.message}
            helperText={t('projects.form.technologies.helperText')}
          />

          <div className="grid md:grid-cols-2 gap-6">
            <Input
              {...projectForm.register('projectUrl')}
              label={t('projects.form.projectUrl.label')}
              type="url"
              placeholder={t('projects.form.projectUrl.placeholder')}
              error={projectForm.formState.errors.projectUrl?.message}
              icon={<ExternalLink className="w-5 h-5" />}
            />
            <Input
              {...projectForm.register('repositoryUrl')}
              label={t('projects.form.repositoryUrl.label')}
              type="url"
              placeholder={t('projects.form.repositoryUrl.placeholder')}
              error={projectForm.formState.errors.repositoryUrl?.message}
              icon={<Github className="w-5 h-5" />}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Input
              {...projectForm.register('startDate')}
              label={t('projects.form.startDate.label')}
              type="month"
              placeholder={t('projects.form.startDate.placeholder')}
              error={projectForm.formState.errors.startDate?.message}
              icon={<Calendar className="w-5 h-5" />}
            />
            <Input
              {...projectForm.register('endDate')}
              label={t('projects.form.endDate.label')}
              type="month"
              placeholder={t('projects.form.endDate.placeholder')}
              error={projectForm.formState.errors.endDate?.message}
              icon={<Calendar className="w-5 h-5" />}
              disabled={projectForm.watch('isCurrent')}
            />
          </div>

          <label className="flex items-center gap-3 text-gray-700 cursor-pointer hover:text-gray-900 transition-colors">
            <input
              type="checkbox"
              {...projectForm.register('isCurrent')}
              className="w-4 h-4 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500"
            />
            {t('projects.form.isCurrent')}
          </label>

          <div className="flex gap-3 justify-end">
            <Button variant="glass" type="button" onClick={() => setIsProjectModalOpen(false)}>
              {t('projects.form.cancelButton')}
            </Button>
            <Button variant="primary" type="submit">
              <Save className="w-5 h-5 mr-2" />
              {t('projects.form.saveButton')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
